import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useRevgenGoalParentUpdateMutation } from './revGenSlice';
import './revgen.scss';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';

interface RevgenMonthlySummaryProps {
    recipientDetails: any;
    goals: any;
    hideControls?: boolean;
}

export const RevgenMonthlySummary = ({
    recipientDetails,
    goals,
    hideControls
}: RevgenMonthlySummaryProps) => {
    const [monthlySummaryData, setMonthlySummaryData] = useState<any>([]);
    const [isGoalAvailable, setIsGoalAvailable] = useState<boolean>(true);
    const [updateGoalParent] = useRevgenGoalParentUpdateMutation();
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [dialogEnable, setDialogEnable] = useState(false);
    const [inputData, setInputData] = useState<any>();
    const [isTlFeedbackEmpty, setIsTlFeedbackEmpty] = useState<boolean>(false);

    useEffect(() => {
        const tableData = [];
        if (goals.length > 0) setIsGoalAvailable(true);
        else setIsGoalAvailable(false);
        const obj = {
            "coaching_goal_parent_id": goals[0]?.coaching_goal_parent_id,
            "supervisor_discussed": goals[0]?.supervisor_discussed,
            "associate_acknowledged": goals[0]?.associate_acknowledged,
            "month_end_reviewed": goals[0]?.month_end_reviewed,
        };
        tableData.push(obj);
        setMonthlySummaryData(tableData);
        goals.forEach((goal: { coachingGoals: any[]; }) => {
            const hasNullMonthEndReviewComment = goal.coachingGoals.some(coachingGoal => coachingGoal.author_month_end_review_comment === null);
            if (hasNullMonthEndReviewComment) {
                setIsTlFeedbackEmpty(true);
            }
            else {
                setIsTlFeedbackEmpty(false);
            }
        })
    }, [goals]);

    const handleSupervisorReview = (rowData: any) => {
        let supervisorchanged = rowData?.supervisor_discussed;
        if (!isGoalAvailable) {
            toast.error('No goals available to discuss');
            return;
        }
        if (recipientDetails?.employeeNumber === loggedEmpNum) {
            toast.error('Only Supervisors are authorized to discuss');
            return;
        }

        if ((rowData?.supervisor_discussed && !rowData?.associate_acknowledged) || !rowData?.supervisor_discussed) {
            supervisorchanged = !rowData?.supervisor_discussed;
            const payload = {
                coachingGoalParentId: rowData?.coaching_goal_parent_id,
                supervisorDiscussed: supervisorchanged,
                parentAuthorNumber: supervisorchanged ? loggedEmpNum : '',
                associateAcknowledged: rowData?.associate_acknowledged,
                monthEndReviewed: rowData?.month_end_reviewed,
            };
            setInputData(payload);
            if (supervisorchanged) {
                setDialogEnable(true);
            }
            else {
                updateGoal(payload);
            }
        }
        else {
            toast.info('Goal cannot be edited after associate has acknowledged.  If the goal has changed, please create a new goal.');
        }
    };
    const updateGoal = (inputData:any) => {
        if (inputData !== undefined) {
            updateGoalParent(inputData)
                .unwrap()
                .then(() => {
                    toast.success('Supervisor Discussed Updated Successfully');
                })
                .catch(() => {
                    toast.error('Failed to save');
                });
        }
    }
    const handleAssociateAck = (rowData: any) => {
        if (!isGoalAvailable) {
            toast.error('No goals available to acknowledge');
            return;
        }
        if (recipientDetails?.employeeNumber !== loggedEmpNum) {
            toast.error('Only Recipients are authorized to acknowledge');
            return;
        }
        if (rowData?.associate_acknowledged || !rowData?.supervisor_discussed) {
            toast.error('Supervisor/Manager must review first');
            return;
        }

        const payload = {
            coachingGoalParentId: rowData?.coaching_goal_parent_id,
            supervisorDiscussed: rowData?.supervisor_discussed,
            associateAcknowledged: true,
            monthEndReviewed: rowData?.month_end_reviewed,
        };
        updateGoalParent(payload)
            .unwrap()
            .then(() => {
                toast.success('Associate (Recipient) acknowledge Updated Successfully');
            })
            .catch((error: any) => {
                console.log(error);
                toast.error('Failed to save');
            });
    };
    const handleMonthEndReview = (rowData: any) => {
        if (!isGoalAvailable) {
            toast.error('No goals available to review');
            return;
        }
        if (rowData?.month_end_reviewed || !rowData?.associate_acknowledged) {
            toast.error('Associate (Recipient) must acknowledge prior to Month-End review');
            return;
        }
        if (recipientDetails?.authorEmployeeNumber !== loggedEmpNum) {
            toast.error('Only Supervisor/Manager are authorized to review');
            return;
        }
       if (isTlFeedbackEmpty) {
            toast.error('Supervisor Feedback for all goals are Required');
            return;
        }

        const payload = {
            coachingGoalParentId: rowData?.coaching_goal_parent_id,
            supervisorDiscussed: rowData?.supervisor_discussed,
            associateAcknowledged: rowData?.associate_acknowledged,
            monthEndReviewed: true,
        };
        updateGoalParent(payload)
            .unwrap()
            .then(() => {
                toast.success('Month-End review Updated Successfully');
            })
            .catch((error: any) => {
                console.log(error);
                toast.error('Failed to save');
            });
    };

    const renderSupervisorReview = (rowData: any, hideControls: boolean |undefined) => {
        if (hideControls) {
            return (
                <Checkbox checked={rowData.supervisor_discussed} disabled />
            );
        } else {
            return (
                <Checkbox checked={rowData.supervisor_discussed} onChange={() => handleSupervisorReview(rowData)} />
            );
        }
    }
    const renderAssociateAck = (rowData: any, hideControls: boolean |undefined) => {
        if (hideControls) {
            return (
                <Checkbox checked={rowData.associate_acknowledged} disabled />
            );
        } else {
            return (
                <Checkbox checked={rowData.associate_acknowledged} onChange={() => handleAssociateAck(rowData)} />
            );
        }
    }
    const renderMonthEndReview = (rowData: any, hideControls: boolean |undefined) => {
        if (hideControls) {
            return (
                <Checkbox checked={rowData.month_end_reviewed} disabled />
            );
        } else {
            return (
                <Checkbox checked={rowData.month_end_reviewed} onChange={() => handleMonthEndReview(rowData)} />
            );
        }
    }
  
    const renderHeader = (col: string) => {
        if (col === 'Supervisor/Manager Reviewed') {
            return (
                <div style={{ fontWeight: 'bolder' }}> {col}</div>
            )
        } else {
            return (
                <div style={{ fontWeight: '400' }}> {col}</div>
            )
        }
    }
    const handleConfirm = () => {
        setDialogEnable(false);
        updateGoal(inputData);
    }
    return (
        <div className="monthlySummary">
            <p>Monthly Goal Summary</p>
            <div className="align-middle text-center ms-5">
                <DataTable
                    value={monthlySummaryData}
                    dataKey="monthly_summary"
                    className="goals-table"
                    rows={5}
                    paginator={monthlySummaryData?.length > 5}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                >
                    <Column field="supervisor_discussed" header={renderHeader('Supervisor/Manager Reviewed')} body={(rowData:any)=>renderSupervisorReview(rowData,hideControls)} />
                    <Column field="associate_acknowledged" header={renderHeader('Recipient Acknowledged')} body={(rowData:any)=>renderAssociateAck(rowData,hideControls)} />
                    <Column field="month_end_reviewed" header={renderHeader('Month End Review')} body={(rowData:any)=>renderMonthEndReview(rowData,hideControls)} />
                </DataTable>
            </div>
            <Dialog
                visible={dialogEnable}
                style={{ width: '40vw', textAlign: 'center' }}
                onHide={() => setDialogEnable(false)}
            >
                <div className="row">
                    <div className="col-md-12 text-center">
                        <p>Did you review the comp plan with the associate? If needed, you can document the conversation in the Conversation tab.</p>
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                label="Cancel"
                                onClick={() => setDialogEnable(false)}
                                className="btn-secondary me-2"
                            />
                            <Button label="Ok" onClick={handleConfirm} className="btn-primary me-2" />
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};
